import React, { CSSProperties, useEffect, useState } from 'react';
import { getMediaUrl, retainImage } from 'utils/media';

interface MediaImageProps {
  path: string;
  alt: string;
  className?: string;
  style?: CSSProperties;
  fallback?: string;
  // Load the image RIGHT AWAY instead of lazily. Lazy-loading defers the fetch
  // until the element is near the viewport, which makes lobby tiles visibly
  // "pop in" after the card has already rendered. Lobby banner art is a small,
  // heavily-repeated set (a handful of distinct URLs the browser caches and
  // dedupes), so eager-loading it is cheap and it appears immediately. Other
  // MediaImage uses keep the default lazy behaviour.
  eager?: boolean;
}

const MediaImage: React.FC<MediaImageProps> = ({
  path,
  alt,
  className,
  style,
  fallback,
  eager = false
}) => {
  const [errored, setErrored] = useState(false);

  // Eager images are the ones that must not flash, so keep them cached for
  // the rest of the page's life — remounting the tile then reuses the image
  // instead of re-downloading it.
  useEffect(() => {
    if (eager && !errored) retainImage(getMediaUrl(path));
  }, [eager, errored, path]);

  if (errored && fallback) {
    return (
      <img
        src={getMediaUrl(fallback)}
        alt={alt}
        className={className}
        style={style}
        onError={() => setErrored(true)}
        loading={eager ? 'eager' : 'lazy'}
        decoding='async'
      />
    );
  }

  return (
    <img
      src={getMediaUrl(path)}
      alt={alt}
      className={className}
      style={style}
      onError={() => setErrored(true)}
      loading={eager ? 'eager' : 'lazy'}
      decoding='async'
    />
  );
};

export default MediaImage;
